import React, { useEffect } from 'react';

const ShardingPartitioning = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Sharding and Partitioning in System Design - Complete Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master database sharding and partitioning strategies for scalable systems. Learn horizontal vs vertical partitioning, sharding techniques, and real-world implementations.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master database sharding and partitioning strategies for scalable systems. Learn horizontal vs vertical partitioning, sharding techniques, and real-world implementations.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'database sharding, partitioning, horizontal scaling, distributed databases, system design, scalability, data distribution');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'database sharding, partitioning, horizontal scaling, distributed databases, system design, scalability, data distribution';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Database Sharding and Partitioning</h1>
        <p style={styles.subtitle}>
          Scale your database horizontally by breaking massive datasets across multiple nodes
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Database Sharding?</h2>
          <p style={styles.paragraph}>
            Database sharding is a method of horizontally partitioning data across multiple database instances or servers. 
            Instead of storing all data in a single database, sharding distributes data across multiple "shards" based on a 
            sharding key, allowing systems to handle massive datasets and high traffic loads that would overwhelm a single 
            database server.
          </p>
          <p style={styles.paragraph}>
            Sharding transforms a single point of failure into a distributed system that can scale horizontally by adding 
            more database nodes. This approach is essential for applications that need to handle millions of users and 
            petabytes of data, such as social media platforms, e-commerce sites, and financial systems.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Horizontal vs Vertical Partitioning</h2>
          <p style={styles.paragraph}>
            <strong>Horizontal Partitioning (Sharding)</strong> splits data by rows across multiple databases. Each shard 
            contains the same schema but different subsets of data. For example, users with IDs 1-1000 might be stored in 
            Shard 1, users with IDs 1001-2000 in Shard 2, and so on.
          </p>
          <p style={styles.paragraph}>
            <strong>Vertical Partitioning</strong> splits data by columns, storing different attributes in separate databases 
            or tables. For example, user authentication data (username, password) might be stored separately from user profile 
            data (bio, preferences, profile picture).
          </p>
          <p style={styles.paragraph}>
            Horizontal partitioning is more commonly referred to as "sharding" and is the primary focus when discussing 
            database scaling strategies. Vertical partitioning is often used to optimize performance by separating frequently 
            accessed data from rarely accessed data.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Common Sharding Strategies</h2>
          
          <h3 style={styles.heading}>Range-Based Sharding</h3>
          <p style={styles.paragraph}>
            Range-based sharding partitions data based on ranges of the sharding key values. This approach is simple to 
            implement and makes range queries efficient, but can create hot spots if data isn't evenly distributed.
          </p>
          <pre style={styles.codeBlock}>
{`// Range-based sharding example
Shard A: user_id 1-10000
Shard B: user_id 10001-20000  
Shard C: user_id 20001-30000

Pros: Simple to implement, range queries are efficient
Cons: Can create hot spots if data isn't evenly distributed`}
          </pre>

          <h3 style={styles.heading}>Hash-Based Sharding</h3>
          <p style={styles.paragraph}>
            Hash-based sharding uses a hash function to determine which shard stores each record. This approach provides 
            better data distribution but makes range queries more complex since related data may be scattered across multiple shards.
          </p>
          <pre style={styles.codeBlock}>
{`// Hash-based sharding example
shard_id = hash(user_id) % number_of_shards

hash(12345) % 3 = 0 → Shard A
hash(67890) % 3 = 1 → Shard B  
hash(11111) % 3 = 2 → Shard C

Pros: Even data distribution
Cons: Range queries become complex`}
          </pre>

          <h3 style={styles.heading}>Directory-Based Sharding</h3>
          <p style={styles.paragraph}>
            Directory-based sharding maintains a lookup service that knows where each piece of data is stored. This approach 
            offers maximum flexibility for data placement optimization but introduces the directory service as a potential 
            bottleneck and single point of failure.
          </p>
          <pre style={styles.codeBlock}>
{`// Directory-based sharding
Directory Service:
user_id: 12345 → Shard A
user_id: 67890 → Shard B
user_id: 11111 → Shard C

Pros: Flexible, can optimize data placement
Cons: Directory service becomes a bottleneck`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Example</h2>
          <p style={styles.paragraph}>
            Here's a practical example of implementing hash-based sharding in a user service. This implementation shows 
            how to route database operations to the appropriate shard based on the user ID.
          </p>
          <pre style={styles.codeBlock}>
{`class DatabaseSharding:
    def __init__(self, shard_configs):
        self.shards = {}
        for shard_id, config in shard_configs.items():
            self.shards[shard_id] = DatabaseConnection(config)
    
    def get_shard_id(self, user_id):
        """Hash-based sharding strategy"""
        return hash(str(user_id)) % len(self.shards)
    
    def get_user(self, user_id):
        shard_id = self.get_shard_id(user_id)
        shard = self.shards[shard_id]
        return shard.query("SELECT * FROM users WHERE user_id = ?", [user_id])
    
    def create_user(self, user_data):
        user_id = user_data['user_id']
        shard_id = self.get_shard_id(user_id)
        shard = self.shards[shard_id]
        return shard.insert("users", user_data)

# Usage
shard_configs = {
    0: {"host": "db1.example.com", "database": "users_shard_0"},
    1: {"host": "db2.example.com", "database": "users_shard_1"},
    2: {"host": "db3.example.com", "database": "users_shard_2"}
}

db_sharding = DatabaseSharding(shard_configs)
user = db_sharding.get_user(12345)  # Routes to appropriate shard`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Challenges and Solutions</h2>
          
          <h3 style={styles.heading}>Cross-Shard Queries</h3>
          <p style={styles.paragraph}>
            Queries that need data from multiple shards become complex and slow. Solutions include denormalizing data to 
            avoid cross-shard queries, using application-level joins, or implementing a query router that can aggregate 
            results from multiple shards.
          </p>

          <h3 style={styles.heading}>Resharding</h3>
          <p style={styles.paragraph}>
            Adding or removing shards requires redistributing existing data, which can be a complex and time-consuming process. 
            Solutions include using consistent hashing to minimize data movement, implementing gradual migration strategies, 
            or designing the system to support dynamic resharding from the beginning.
          </p>

          <h3 style={styles.heading}>Hot Spots</h3>
          <p style={styles.paragraph}>
            Some shards may receive disproportionately more traffic than others, creating performance bottlenecks. Solutions 
            include choosing sharding keys that distribute load evenly, monitoring shard performance continuously, and 
            implementing shard splitting for overloaded shards.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Instagram's Sharding Strategy</h3>
          <p style={styles.paragraph}>
            Instagram uses a modified hash-based sharding approach where they shard by user_id. They use thousands of 
            database shards to handle their massive user base, with each shard containing photos and metadata for a subset 
            of users. This approach allows them to scale horizontally while maintaining good performance for user-specific queries.
          </p>

          <h3 style={styles.heading}>Discord's Message Sharding</h3>
          <p style={styles.paragraph}>
            Discord shards messages by channel_id, ensuring that all messages in a channel are stored together. This makes 
            it efficient to fetch message history while distributing load across multiple database clusters. They can easily 
            scale by adding more shards as new channels are created.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right sharding key:</strong> Select a key that distributes data evenly and aligns with 
              your most common query patterns.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for resharding:</strong> Design your system to handle adding or removing shards without downtime 
              by using techniques like consistent hashing or virtual shards.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor shard health:</strong> Track performance metrics for each shard to identify bottlenecks, 
              hot spots, and uneven load distribution.
            </li>
            <li style={styles.listItem}>
              <strong>Implement connection pooling:</strong> Use connection pools to manage database connections efficiently 
              and avoid overwhelming individual shards.
            </li>
            <li style={styles.listItem}>
              <strong>Consider read replicas:</strong> Use read replicas for each shard to distribute read traffic and 
              improve query performance.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Database sharding is a powerful technique for scaling databases horizontally, but it comes with significant 
            complexity. The key is to plan your sharding strategy carefully, choose appropriate sharding keys, and design 
            your application to handle the distributed nature of sharded data.
          </p>
          <p style={styles.paragraph}>
            Remember that sharding should only be implemented when necessary. Start with vertical scaling, read replicas, 
            and caching before implementing sharding, as the operational complexity can be significant. When you do need 
            to shard, invest time in proper planning and monitoring to ensure your sharded system remains performant and maintainable.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ShardingPartitioning;